import React, { useState, useContext, useEffect, useMemo } from 'react';
import { Table, Form, Button, Row, Col, Modal, Dropdown, ButtonGroup, InputGroup, Spinner } from 'react-bootstrap';
import { useSnackbar } from 'notistack';
import { ProductContext } from '../../ProductProvider';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import './JournalIssues.css';
import ArticlesForm from './ArticlesForm';

const config = require('../../config.json');

export default function JournalIssues(props) {
  const { enqueueSnackbar } = useSnackbar();
  const { journalData, loggedUser, articleData, setArticleData } = useContext(ProductContext);
  const { viewOnly, formType, handleTabSelect, setTabArticles } = props;

  // Issues Data State
  const [searchResult, setSearchResult] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  // Filter, Search, and View Controls
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [volumeFilter, setVolumeFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState('volIssueDesc'); // Default: Descending
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'

  // Modal States
  const [showIndModal, setShowIndModal] = useState(false);
  const [issueModalMode, setIssueModalMode] = useState('Add'); // 'Add' | 'Edit'
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Quick Receive Modal States
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [receiveTargetIssue, setReceiveTargetIssue] = useState(null);
  const [receiveModalDate, setReceiveModalDate] = useState('');
  const [isReceivingSubmit, setIsReceivingSubmit] = useState(false);

  // Delete State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [issueToDelete, setIssueToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form States
  const initialIssueState = {
    journalID: '',
    journalTitle: '',
    frequency: '',
    volNo: '',
    issueNumber: '',
    volissuesNo: '',
    issueYear: new Date().getFullYear().toString(),
    issueMonth: (new Date().getMonth() + 1).toString(),
    pubDate: new Date().toISOString().split('T')[0],
    issueStatus: 'Expected',
    receivedDate: '',
    seq: '',
    issuedTo: '',
    expDate: new Date().toISOString().split('T')[0]
  };

  const [newIssue, setNewIssue] = useState(initialIssueState);

  const [bulkGeneration, setBulkGeneration] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    volNo: '',
    startIssueNumber: '1',
    endIssueNumber: '12',
  });

  // Modal controls
  const handleIndClose = () => setShowIndModal(false);
  const handleBulkClose = () => setShowBulkModal(false);
  const handleArticleClose = () => setShowArticleModal(false);
  const handleReceiveModalClose = () => {
    if (isReceivingSubmit) return;
    setShowReceiveModal(false);
    setReceiveTargetIssue(null);
    setReceiveModalDate('');
  };

  const handleDeleteClick = (e, issue) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIssueToDelete(issue);
    setShowDeleteModal(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setIssueToDelete(null);
  };

  // Robust Descending / Custom Sorter
  const sortIssuesList = (items, sortType = 'volIssueDesc') => {
    return [...items].sort((a, b) => {
      const volA = Number(a.volNo ?? a.volume ?? 0);
      const volB = Number(b.volNo ?? b.volume ?? 0);
      const issA = Number(a.issueNumber ?? a.seq ?? 0);
      const issB = Number(b.issueNumber ?? b.seq ?? 0);

      switch (sortType) {
        case 'volIssueDesc': // Descending order (Default)
          if (!isNaN(volA) && !isNaN(volB) && volB !== volA) {
            return volB - volA;
          }
          if (!isNaN(issA) && !isNaN(issB) && issB !== issA) {
            return issB - issA;
          }
          return (b.pubDate || b.expDate || '').localeCompare(a.pubDate || a.expDate || '');

        case 'volIssueAsc': // Ascending order
          if (!isNaN(volA) && !isNaN(volB) && volA !== volB) {
            return volA - volB;
          }
          if (!isNaN(issA) && !isNaN(issB) && issA !== issB) {
            return issA - issB;
          }
          return (a.pubDate || a.expDate || '').localeCompare(b.pubDate || b.expDate || '');

        case 'pubDateDesc':
          return (b.pubDate || b.expDate || '').localeCompare(a.pubDate || a.expDate || '');

        case 'pubDateAsc':
          return (a.pubDate || a.expDate || '').localeCompare(b.pubDate || b.expDate || '');

        case 'status':
          const priority = { Missing: 1, Damaged: 2, Expected: 3, Pending: 4, Received: 5, Available: 6 };
          const pA = priority[a.issueStatus] || 99;
          const pB = priority[b.issueStatus] || 99;
          return pA - pB;

        default:
          return 0;
      }
    });
  };

  // Fetch Issues from Backend API
  const fetchIssues = async () => {
    if (!journalData?.journalID) return;
    try {
      setLoaded(false);
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const clientID = "VJCollege";
      const searchTopic = "JournalID";
      const searchValue = journalData.journalID;

      const response = await axios.get(`${config.api.invokeURL}/vjlibrary/search/journal/issues`, {
        params: {
          fetchKey: searchValue,
          fetchOption: searchTopic,
          clientID: clientID
        },
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      const rawData = Array.isArray(response.data) ? response.data : [];
      // Apply default descending sort
      const sortedData = sortIssuesList(rawData, sortBy);
      setSearchResult(sortedData);
      setLoaded(true);
    } catch (error) {
      console.error("Error fetching issues:", error);
      enqueueSnackbar("Error fetching issues: " + (error.response?.data?.message || error.message), { variant: 'warning' });
      setLoaded(true);
    }
  };

  useEffect(() => {
    fetchIssues();
  }, [journalData?.journalID]);

  // Handle Sort Change
  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setSearchResult(prev => sortIssuesList(prev, newSort));
  };

  // Calculate Unique Volumes for Filter
  const uniqueVolumes = useMemo(() => {
    const vols = new Set();
    searchResult.forEach(item => {
      const v = item.volNo ?? item.volume;
      if (v !== undefined && v !== null && v !== '') {
        vols.add(v.toString());
      }
    });
    return Array.from(vols).sort((a, b) => Number(b) - Number(a));
  }, [searchResult]);

  // Filtered Issues for Display
  const filteredIssues = useMemo(() => {
    return searchResult.filter(issue => {
      // Search term matching
      if (searchTerm.trim() !== '') {
        const term = searchTerm.toLowerCase();
        const vol = (issue.volNo ?? issue.volume ?? '').toString().toLowerCase();
        const iss = (issue.issueNumber ?? '').toString().toLowerCase();
        const year = (issue.issueYear ?? issue.year ?? '').toString().toLowerCase();
        const status = (issue.issueStatus ?? '').toString().toLowerCase();
        const issuedTo = (issue.issuedTo ?? '').toString().toLowerCase();
        const seq = (issue.seq ?? '').toString().toLowerCase();
        const pubDate = (issue.pubDate ?? '').toString().toLowerCase();

        const matches = vol.includes(term) ||
          iss.includes(term) ||
          year.includes(term) ||
          status.includes(term) ||
          issuedTo.includes(term) ||
          seq.includes(term) ||
          pubDate.includes(term) ||
          `vol ${vol}`.includes(term) ||
          `issue ${iss}`.includes(term);

        if (!matches) return false;
      }

      // Status filter
      if (statusFilter !== 'ALL') {
        if (statusFilter === 'Expected' && (issue.issueStatus === 'Expected' || issue.issueStatus === 'Pending')) {
          // match either expected or pending
        } else if (issue.issueStatus !== statusFilter) {
          return false;
        }
      }

      // Volume filter
      if (volumeFilter !== 'ALL') {
        const v = (issue.volNo ?? issue.volume ?? '').toString();
        if (v !== volumeFilter) {
          return false;
        }
      }

      return true;
    });
  }, [searchResult, searchTerm, statusFilter, volumeFilter]);

  // Summary Metrics (KPIs)
  const stats = useMemo(() => {
    const total = searchResult.length;
    const received = searchResult.filter(i => i.issueStatus === 'Received').length;
    const expected = searchResult.filter(i => i.issueStatus === 'Expected' || i.issueStatus === 'Pending').length;
    const missing = searchResult.filter(i => i.issueStatus === 'Missing' || i.issueStatus === 'Damaged').length;
    const receivedPct = total > 0 ? Math.round((received / total) * 100) : 0;

    return { total, received, expected, missing, receivedPct };
  }, [searchResult]);

  // Fast "Mark as Received" Handler - Opens confirmation popup with editable received date
  const handleQuickReceive = (e, issue) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    const today = new Date().toISOString().split('T')[0];
    setReceiveTargetIssue(issue);
    // Received date as today by default or existing receivedDate, fully editable
    setReceiveModalDate(issue.receivedDate || today);
    setShowReceiveModal(true);
  };

  // Submit Receive Action from Popup Modal
  const handleConfirmReceive = async (e) => {
    if (e) e.preventDefault();
    if (!receiveTargetIssue) return;

    const issue = receiveTargetIssue;
    const issueKey = issue.volissuesNo || `${issue.volNo}-${issue.issueNumber}`;
    const today = new Date().toISOString().split('T')[0];
    const finalReceivedDate = receiveModalDate || today;

    try {
      setIsReceivingSubmit(true);
      setActionLoadingId(issueKey);
      const { idToken } = (await fetchAuthSession()).tokens ?? {};

      const currentJournalTitle = issue.journalTitle || journalData?.journalTitle || journalData?.title || '';
      const currentJournalID = journalData?.journalID || issue.journalID || '';
      const currentFrequency = journalData?.frequency || issue.frequency || '';

      const params = {
        journalID: currentJournalID,
        journalTitle: currentJournalTitle,
        frequency: currentFrequency,
        expDate: issue.expDate || today,
        issueNumber: issue.issueNumber,
        issueMonth: issue.issueMonth || issue.month || '',
        pubDate: issue.pubDate || '',
        issueStatus: 'Received',
        receivedDate: finalReceivedDate,
        seq: issue.seq || '',
        issuedTo: issue.issuedTo || '',
        volNo: issue.volNo ?? issue.volume,
        volissuesNo: issue.volissuesNo || `${issue.volNo}${issue.issueNumber}`,
        volume: issue.volNo ?? issue.volume,
        issueYear: issue.issueYear || issue.year || '',
        clientID: loggedUser?.website || "VJCollege",
        updatedBy: loggedUser?.nickname || "Librarian",
        updatedOn: new Date().toISOString()
      };

      await axios.patch(`${config.api.invokeURL}/vjlibrary/journal/catalogue/issues`, params, {
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      enqueueSnackbar(`Vol ${issue.volNo}, Issue #${issue.issueNumber} marked as Received on ${finalReceivedDate}!`, { variant: 'success' });
      setShowReceiveModal(false);
      setReceiveTargetIssue(null);
      await fetchIssues();
    } catch (error) {
      console.error('Error updating status:', error);
      enqueueSnackbar('Error updating issue: ' + (error.response?.data?.message || error.message), { variant: 'error' });
    } finally {
      setIsReceivingSubmit(false);
      setActionLoadingId(null);
    }
  };

  // Open Modal to Add Single Issue
  const handleOpenAddIssue = () => {
    // Auto-compute next issue number suggestion if available
    let nextVol = '1';
    let nextIss = '1';
    if (searchResult.length > 0) {
      const latest = searchResult[0];
      nextVol = (latest.volNo ?? latest.volume ?? '1').toString();
      nextIss = (Number(latest.issueNumber || 0) + 1).toString();
    }

    const currentJournalTitle = journalData?.journalTitle || journalData?.title || '';
    const currentJournalID = journalData?.journalID || '';
    const currentFrequency = journalData?.frequency || '';
    const today = new Date().toISOString().split('T')[0];

    setNewIssue({
      ...initialIssueState,
      journalID: currentJournalID,
      journalTitle: currentJournalTitle,
      frequency: currentFrequency,
      volNo: nextVol,
      issueNumber: nextIss,
      volissuesNo: currentJournalID ? `${currentJournalID}-${nextVol}${nextIss}` : `${nextVol}${nextIss}`,
      receivedDate: today
    });
    setIssueModalMode('Add');
    setShowIndModal(true);
  };

  // Open Modal to Edit Issue
  const handleEditIssue = (e, issue) => {
    e.preventDefault();
    const today = new Date().toISOString().split('T')[0];
    setNewIssue({
      volNo: issue.volNo ?? issue.volume ?? '',
      issueNumber: issue.issueNumber ?? '',
      issueYear: issue.issueYear ?? issue.year ?? new Date().getFullYear().toString(),
      issueMonth: issue.issueMonth ?? issue.month ?? (new Date().getMonth() + 1).toString(),
      journalID: issue.journalID || journalData?.journalID || '',
      journalTitle: issue.journalTitle || journalData?.journalTitle || journalData?.title || '',
      frequency: issue.frequency || journalData?.frequency || '',
      pubDate: issue.pubDate ?? '',
      issueStatus: issue.issueStatus ?? 'Expected',
      receivedDate: issue.receivedDate || today,
      seq: issue.seq ?? '',
      issuedTo: issue.issuedTo ?? '',
      expDate: issue.expDate ?? '',
      volissuesNo: issue.volissuesNo || `${issue.volNo}${issue.issueNumber}`
    });
    setIssueModalMode('Edit');
    setShowIndModal(true);
  };

  // Submit Single Issue (Add or Edit)
  const handleSaveIssue = async (e) => {
    e.preventDefault();
    if (!newIssue.volNo || !newIssue.issueNumber) {
      enqueueSnackbar('Volume and Issue Number are required.', { variant: 'warning' });
      return;
    }

    try {
      setIsSubmitting(true);
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const currentJournalTitle = journalData?.journalTitle || journalData?.title || newIssue.journalTitle || '';
      const currentJournalID = journalData?.journalID || newIssue.journalID || '';
      const currentFrequency = journalData?.frequency || newIssue.frequency || '';
      const today = new Date().toISOString().split('T')[0];
      const finalReceivedDate = newIssue.receivedDate || (newIssue.issueStatus === 'Received' ? today : '');

      if (issueModalMode === 'Add') {
        const volissuesNo = currentJournalID
          ? `${currentJournalID}-${newIssue.volNo}${newIssue.issueNumber}`
          : (newIssue.volissuesNo || `${newIssue.volNo}${newIssue.issueNumber}`);

        const params = {
          journalID: currentJournalID,
          journalTitle: currentJournalTitle,
          frequency: currentFrequency,
          issues: [{
            ...newIssue,
            receivedDate: finalReceivedDate,
            journalID: currentJournalID,
            journalTitle: currentJournalTitle,
            frequency: currentFrequency,
            volissuesNo: volissuesNo,
            volume: newIssue.volNo,
            volNo: newIssue.volNo
          }],
          clientID: loggedUser?.website || "VJCollege",
          updatedBy: loggedUser?.nickname || "Librarian",
          updatedOn: new Date().toISOString()
        };
        // console.log("Add Issue params:", params);

        await axios.post(`${config.api.invokeURL}/vjlibrary/journal/catalogue/issues`, params, {
          headers: {
            Authorization: idToken,
            'x-api-key': config.api.key
          }
        });
        enqueueSnackbar(`Issue Vol ${newIssue.volNo} #${newIssue.issueNumber} added successfully!`, { variant: 'success' });
      } else {
        const params = {
          journalID: currentJournalID,
          journalTitle: currentJournalTitle,
          frequency: currentFrequency,
          expDate: newIssue.expDate,
          issueNumber: newIssue.issueNumber,
          issueMonth: newIssue.issueMonth,
          pubDate: newIssue.pubDate,
          issueStatus: newIssue.issueStatus,
          issuedTo: newIssue.issuedTo,
          receivedDate: finalReceivedDate,
          seq: newIssue.seq,
          volNo: newIssue.volNo,
          volissuesNo: newIssue.volissuesNo || (currentJournalID ? `${currentJournalID}-${newIssue.volNo}${newIssue.issueNumber}` : `${newIssue.volNo}${newIssue.issueNumber}`),
          volume: newIssue.volNo,
          issueYear: newIssue.issueYear,
          clientID: loggedUser?.website || "VJCollege",
          updatedBy: loggedUser?.nickname || "Librarian",
          updatedOn: new Date().toISOString()
        };
        // console.log("Edit Issue params:", params);

        await axios.patch(`${config.api.invokeURL}/vjlibrary/journal/catalogue/issues`, params, {
          headers: {
            Authorization: idToken,
            'x-api-key': config.api.key
          }
        });
        enqueueSnackbar(`Issue Vol ${newIssue.volNo} #${newIssue.issueNumber} updated successfully!`, { variant: 'success' });
      }

      setShowIndModal(false);
      await fetchIssues();
    } catch (error) {
      console.error('Error saving issue:', error);
      enqueueSnackbar('Error saving issue: ' + (error.response?.data?.message || error.message), { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Bulk Issue Generation Helper
  const calculateBulkPreview = () => {
    if (!bulkGeneration.startDate || !bulkGeneration.endIssueNumber || !bulkGeneration.volNo) {
      return null;
    }
    const startNum = parseInt(bulkGeneration.startIssueNumber) || 1;
    const endNum = parseInt(bulkGeneration.endIssueNumber) || 1;
    const count = endNum >= startNum ? (endNum - startNum + 1) : 0;
    return { count, startNum, endNum };
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    if (!bulkGeneration.volNo || !bulkGeneration.startDate || !bulkGeneration.endIssueNumber) {
      enqueueSnackbar('Please fill all required bulk generation fields.', { variant: 'warning' });
      return;
    }

    try {
      setIsSubmitting(true);
      const start = new Date(bulkGeneration.startDate);
      const end = bulkGeneration.endDate ? new Date(bulkGeneration.endDate) : null;
      const generatedIssues = [];
      let currentDate = new Date(start);
      let currentIssueNumber = parseInt(bulkGeneration.startIssueNumber) || 1;
      const endIssueNumber = parseInt(bulkGeneration.endIssueNumber);

      const currentJournalTitle = journalData?.journalTitle || journalData?.title || '';
      const currentJournalID = journalData?.journalID || '';
      const currentFrequency = journalData?.frequency || '';

      const getNextDate = (date) => {
        const next = new Date(date);
        switch (journalData.frequency) {
          case 'Weekly':
            next.setDate(next.getDate() + 7);
            break;
          case 'Fortnightly':
            next.setDate(next.getDate() + 14);
            break;
          case 'Monthly':
            next.setMonth(next.getMonth() + 1);
            break;
          case 'Bimonthly':
            next.setMonth(next.getMonth() + 2);
            break;
          case 'Quarterly':
            next.setMonth(next.getMonth() + 3);
            break;
          case 'Biannual':
            next.setMonth(next.getMonth() + 6);
            break;
          case 'Annual':
            next.setFullYear(next.getFullYear() + 1);
            break;
          default:
            next.setMonth(next.getMonth() + 1);
        }
        return next;
      };

      while (currentIssueNumber <= endIssueNumber) {
        if (end && currentDate > end) break;

        const dateStr = currentDate.toISOString().split('T')[0];
        const volissuesNo = currentJournalID
          ? `${currentJournalID}-${bulkGeneration.volNo}${currentIssueNumber}`
          : `${bulkGeneration.volNo}${currentIssueNumber}`;

        generatedIssues.push({
          journalID: currentJournalID,
          journalTitle: currentJournalTitle,
          frequency: currentFrequency,
          volNo: bulkGeneration.volNo.toString(),
          volume: bulkGeneration.volNo.toString(),
          issueNumber: currentIssueNumber.toString(),
          volissuesNo: volissuesNo,
          year: currentDate.getFullYear().toString(),
          issueYear: currentDate.getFullYear().toString(),
          month: (currentDate.getMonth() + 1).toString(),
          issueMonth: (currentDate.getMonth() + 1).toString(),
          pubDate: dateStr,
          issueStatus: 'Expected',
          receivedDate: '',
          seq: currentIssueNumber.toString(),
          issuedTo: '',
          expDate: dateStr,
        });

        currentDate = getNextDate(currentDate);
        currentIssueNumber++;
      }

      if (generatedIssues.length === 0) {
        enqueueSnackbar('No issues could be generated with the given criteria.', { variant: 'warning' });
        setIsSubmitting(false);
        return;
      }

      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const params = {
        journalID: currentJournalID,
        journalTitle: currentJournalTitle,
        frequency: currentFrequency,
        issues: generatedIssues,
        clientID: loggedUser?.website || "VJCollege",
        updatedBy: loggedUser?.nickname || "Librarian",
        updatedOn: new Date().toISOString().split('T')[0]
      };
      // console.log("Bulk Params:", params);
      await axios.post(`${config.api.invokeURL}/vjlibrary/journal/catalogue/issues`, params, {
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      enqueueSnackbar(`Successfully generated ${generatedIssues.length} issues in descending order!`, { variant: 'success' });
      setShowBulkModal(false);
      await fetchIssues();
    } catch (error) {
      console.error('Error generating issues:', error);
      enqueueSnackbar('Error generating bulk issues: ' + (error.response?.data?.message || error.message), { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Article Action Handlers
  const handleAddArticle = (e, issue) => {
    e.preventDefault();
    setArticleData({
      ...articleData,
      journalID: issue.journalID || journalData?.journalID,
      journalTitle: issue.journalTitle || journalData?.journalTitle || journalData?.title || '',
      volissuesNo: issue.volissuesNo || `${issue.volNo}${issue.issueNumber}`
    });
    setShowArticleModal(true);
  };

  const handleViewArticles = (e, issue) => {
    e.preventDefault();
    setArticleData({
      ...articleData,
      journalID: issue.journalID || journalData?.journalID,
      journalTitle: issue.journalTitle || journalData?.journalTitle || journalData?.title || '',
      volissuesNo: issue.volissuesNo || `${issue.volNo}${issue.issueNumber}`
    });
    if (setTabArticles) setTabArticles(false);
    if (handleTabSelect) handleTabSelect('Articles');
  };

  // Delete Issue & Associated Articles Handler
  const handleConfirmDelete = async () => {
    if (!issueToDelete) return;
    setIsDeleting(true);
    try {
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const clientID = loggedUser?.website || issueToDelete?.clientID || 'VJCollege';
      const volissuesNo = issueToDelete.volissuesNo || `${issueToDelete.volNo}${issueToDelete.issueNumber}`;

      const payload = {
        clientID: clientID,
        volissuesNo: volissuesNo
      };

      //  console.log('Attempting to delete issue with payload:', payload);

      await axios.post(`${config.api.invokeURL}/vjlibrary/journal/catalogue/issues/delete`, payload, {
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      enqueueSnackbar(`Issue Vol ${issueToDelete.volNo ?? issueToDelete.volume ?? ''}, Issue #${issueToDelete.issueNumber ?? ''} (${volissuesNo}) and its articles deleted successfully!`, {
        variant: 'success'
      });
      setShowDeleteModal(false);
      setIssueToDelete(null);
      await fetchIssues();
    } catch (error) {
      console.error('Error deleting issue:', error);
      const serverMessage = error.response?.data?.message || error.response?.data || error.message;
      enqueueSnackbar(`Error deleting issue: ${serverMessage}`, {
        variant: 'error'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Status Badge UI Helper
  const renderStatusBadge = (status) => {
    switch (status) {
      case 'Received':
        return (
          <span className="badge-status badge-status-received">
            <i className="bi bi-check-circle-fill"></i> Received
          </span>
        );
      case 'Expected':
      case 'Pending':
        return (
          <span className="badge-status badge-status-expected">
            <i className="bi bi-clock-fill"></i> {status}
          </span>
        );
      case 'Missing':
        return (
          <span className="badge-status badge-status-missing">
            <i className="bi bi-exclamation-triangle-fill"></i> Missing
          </span>
        );
      case 'Damaged':
        return (
          <span className="badge-status badge-status-damaged">
            <i className="bi bi-shield-slash-fill"></i> Damaged
          </span>
        );
      default:
        return (
          <span className="badge-status badge-status-available">
            <i className="bi bi-journal-bookmark-fill"></i> {status || 'Available'}
          </span>
        );
    }
  };

  const preview = calculateBulkPreview();

  return (
    <div className="journal-issues-wrapper mt-3">
      {/* Journal Context & Quick Info Banner */}
      <div className="journal-header-card d-flex flex-wrap justify-content-between align-items-center gap-3">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <span className="journal-header-badge">
              <i className="bi bi-journal-text me-1"></i>
              {journalData?.journalID || 'Journal'}
            </span>
            <span className="journal-header-badge">
              <i className="bi bi-arrow-repeat me-1"></i>
              {journalData?.frequency || 'Monthly'}
            </span>
            {journalData?.issn && (
              <span className="journal-header-badge">
                ISSN: {journalData.issn}
              </span>
            )}
          </div>
          <div className="journal-header-title">
            {journalData?.journalTitle || journalData?.title || 'Journal Issues Management'}
          </div>
          {journalData?.publisher && (
            <div className="text-white-50 small">
              <i className="bi bi-building me-1"></i> Publisher: {journalData.publisher}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {!viewOnly && (
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="light"
              className="fw-bold shadow-sm"
              onClick={handleOpenAddIssue}
              style={{ borderRadius: '8px' }}
            >
              <i className="bi bi-plus-circle me-1 text-primary"></i> Single Issue
            </Button>
            <Button
              variant="outline-light"
              className="fw-bold shadow-sm"
              onClick={() => setShowBulkModal(true)}
              style={{ borderRadius: '8px' }}
            >
              <i className="bi bi-lightning-charge me-1"></i> Bulk Generate
            </Button>
          </div>
        )}
      </div>

      {/* KPI Stats Overview */}
      <div className="stats-grid">
        <div className="stat-card stat-card-total">
          <div className="stat-card-label">Total Issues</div>
          <div className="stat-card-value">{stats.total}</div>
          <div className="stat-card-subtext">Sorted descending</div>
        </div>

        <div className="stat-card stat-card-received">
          <div className="stat-card-label">Received In Library</div>
          <div className="stat-card-value text-success">{stats.received}</div>
          <div className="stat-card-subtext">{stats.receivedPct}% of total issues</div>
        </div>

        <div className="stat-card stat-card-expected">
          <div className="stat-card-label">Expected / In-Transit</div>
          <div className="stat-card-value text-warning">{stats.expected}</div>
          <div className="stat-card-subtext">Awaiting delivery</div>
        </div>

        <div className="stat-card stat-card-missing">
          <div className="stat-card-label">Missing / Claims</div>
          <div className="stat-card-value text-danger">{stats.missing}</div>
          <div className="stat-card-subtext">Requires follow-up</div>
        </div>
      </div>

      {/* Interactive Toolbar: Search, Filters, Sorters & View Switcher */}
      <div className="toolbar-container">
        <Row className="g-3 align-items-center">
          {/* Search Box */}
          <Col lg={4} md={6} sm={12}>
            <InputGroup className="search-input-group">
              <InputGroup.Text>
                <i className="bi bi-search"></i>
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search vol, issue #, year, status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button variant="outline-secondary" onClick={() => setSearchTerm('')}>
                  <i className="bi bi-x"></i>
                </Button>
              )}
            </InputGroup>
          </Col>

          {/* Volume Filter Dropdown */}
          <Col lg={2} md={3} sm={6}>
            <Form.Select
              size="sm"
              value={volumeFilter}
              onChange={(e) => setVolumeFilter(e.target.value)}
              className="py-2"
            >
              <option value="ALL">All Volumes ({uniqueVolumes.length})</option>
              {uniqueVolumes.map(vol => (
                <option key={vol} value={vol}>Volume {vol}</option>
              ))}
            </Form.Select>
          </Col>

          {/* Sort By Dropdown */}
          <Col lg={3} md={3} sm={6}>
            <Form.Select
              size="sm"
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
              className="py-2 fw-semibold"
            >
              <option value="volIssueDesc">▼ Vol & Issue (Newest / Desc)</option>
              <option value="volIssueAsc">▲ Vol & Issue (Oldest / Asc)</option>
              <option value="pubDateDesc">▼ Pub Date (Latest first)</option>
              <option value="pubDateAsc">▲ Pub Date (Earliest first)</option>
              <option value="status">★ Status Priority (Missing first)</option>
            </Form.Select>
          </Col>

          {/* View Mode & Refresh */}
          <Col lg={3} md={12} className="d-flex justify-content-lg-end justify-content-between align-items-center gap-2">
            <ButtonGroup size="sm">
              <Button
                variant={viewMode === 'grid' ? 'primary' : 'outline-secondary'}
                onClick={() => setViewMode('grid')}
                title="Grid Cards View"
              >
                <i className="bi bi-grid-fill me-1"></i> Cards
              </Button>
              <Button
                variant={viewMode === 'table' ? 'primary' : 'outline-secondary'}
                onClick={() => setViewMode('table')}
                title="Dense Table View"
              >
                <i className="bi bi-table me-1"></i> Table
              </Button>
            </ButtonGroup>

            <Button
              variant="outline-secondary"
              size="sm"
              onClick={fetchIssues}
              title="Refresh Issues from server"
            >
              <i className="bi bi-arrow-clockwise"></i> Refresh
            </Button>
          </Col>
        </Row>

        {/* Status Filter Pills */}
        <div className="d-flex flex-wrap gap-2 mt-3 pt-2 border-top">
          <span className="small text-muted align-self-center me-1">Filter Status:</span>
          <span
            className={`filter-pill ${statusFilter === 'ALL' ? 'active' : ''}`}
            onClick={() => setStatusFilter('ALL')}
          >
            All ({searchResult.length})
          </span>
          <span
            className={`filter-pill filter-pill-received ${statusFilter === 'Received' ? 'active' : ''}`}
            onClick={() => setStatusFilter('Received')}
          >
            <i className="bi bi-check-circle me-1"></i> Received ({stats.received})
          </span>
          <span
            className={`filter-pill filter-pill-expected ${statusFilter === 'Expected' ? 'active' : ''}`}
            onClick={() => setStatusFilter('Expected')}
          >
            <i className="bi bi-clock me-1"></i> Expected ({stats.expected})
          </span>
          <span
            className={`filter-pill filter-pill-missing ${statusFilter === 'Missing' ? 'active' : ''}`}
            onClick={() => setStatusFilter('Missing')}
          >
            <i className="bi bi-exclamation-triangle me-1"></i> Missing / Claims ({stats.missing})
          </span>
        </div>
      </div>

      {/* Main Content Area */}
      {!loaded ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" role="status" />
          <div className="text-muted mt-2 small">Loading journal issues...</div>
        </div>
      ) : searchResult.length === 0 ? (
        /* Empty State (No issues in database yet) */
        <div className="empty-issues-box">
          <div className="empty-issues-icon">
            <i className="bi bi-journals"></i>
          </div>
          <div className="empty-issues-title">No Issues Found for this Journal</div>
          <div className="empty-issues-desc">
            You haven't generated or catalogued any issues for <strong>{journalData?.journalTitle || 'this journal'}</strong> yet. You can automatically generate multiple issues or add them one by one.
          </div>
          {!viewOnly && (
            <div className="d-flex justify-content-center gap-3">
              <Button className="journal-btn" onClick={() => setShowBulkModal(true)}>
                <i className="bi bi-lightning-charge me-1"></i> Bulk Generate Issues
              </Button>
              <Button variant="outline-primary" onClick={handleOpenAddIssue}>
                <i className="bi bi-plus-lg me-1"></i> Add Single Issue
              </Button>
            </div>
          )}
        </div>
      ) : filteredIssues.length === 0 ? (
        /* Filtered Empty State */
        <div className="empty-issues-box">
          <div className="empty-issues-icon text-secondary">
            <i className="bi bi-funnel"></i>
          </div>
          <div className="empty-issues-title">No Matching Issues Found</div>
          <div className="empty-issues-desc">
            No issues match your current filter criteria: "{searchTerm || statusFilter || volumeFilter}". Try resetting your filters.
          </div>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('ALL');
              setVolumeFilter('ALL');
            }}
          >
            Reset All Filters
          </Button>
        </div>
      ) : viewMode === 'grid' ? (
        /* ================= CARDS GRID VIEW ================= */
        <div className="row g-3">
          {filteredIssues.map((issue, index) => {
            const statusKey = issue.issueStatus || 'Available';
            const accentClass =
              statusKey === 'Received'
                ? 'accent-received'
                : statusKey === 'Expected' || statusKey === 'Pending'
                  ? 'accent-expected'
                  : statusKey === 'Missing'
                    ? 'accent-missing'
                    : statusKey === 'Damaged'
                      ? 'accent-damaged'
                      : 'accent-available';

            const issueKey = issue.volissuesNo || `${issue.volNo}-${issue.issueNumber}`;
            const isRowReceiving = actionLoadingId === issueKey;

            return (
              <div key={issue.volissuesNo || index} className="col-12 col-md-6 col-lg-4 col-xl-3">
                <div className="issue-card">
                  <div className={`issue-card-top-accent ${accentClass}`} />

                  {/* Header Row: Status Badge & Dropdown Actions */}
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-2 pt-1">
                      {renderStatusBadge(issue.issueStatus)}

                      <Dropdown align="end">
                        <Dropdown.Toggle
                          variant="light"
                          size="sm"
                          className="border-0 p-1 text-muted"
                          id={`dropdown-issue-${index}`}
                        >
                          <i className="bi bi-three-dots-vertical"></i>
                        </Dropdown.Toggle>

                        <Dropdown.Menu className="shadow-sm">
                          {!viewOnly && (
                            <Dropdown.Item onClick={(e) => handleAddArticle(e, issue)}>
                              <i className="bi bi-file-earmark-plus me-2 text-primary"></i> Add Article
                            </Dropdown.Item>
                          )}
                          <Dropdown.Item onClick={(e) => handleViewArticles(e, issue)}>
                            <i className="bi bi-files me-2 text-info"></i> View Articles
                          </Dropdown.Item>
                          {!viewOnly && (
                            <>
                              <Dropdown.Divider />
                              <Dropdown.Item onClick={(e) => handleEditIssue(e, issue)}>
                                <i className="bi bi-pencil-square me-2 text-warning"></i> Edit Issue
                              </Dropdown.Item>
                              <Dropdown.Divider />
                              <Dropdown.Item onClick={(e) => handleDeleteClick(e, issue)} className="text-danger">
                                <i className="bi bi-trash me-2 text-danger"></i> Delete Issue & Articles
                              </Dropdown.Item>
                            </>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>

                    {/* Volume & Issue Number Title */}
                    <div className="issue-card-title">
                      Volume {issue.volNo ?? issue.volume ?? '-'}, Issue #{issue.issueNumber ?? '-'}
                    </div>
                    <div className="issue-card-volissues mb-3">
                      Code: {issue.volissuesNo || `${issue.volNo}${issue.issueNumber}`}
                      {issue.seq ? ` • Seq: ${issue.seq}` : ''}
                    </div>

                    {/* Metadata Items */}
                    <div className="mb-2">
                      <div className="issue-meta-item">
                        <span className="issue-meta-label">
                          <i className="bi bi-calendar3 me-1 text-muted"></i> Pub Date:
                        </span>
                        <span className="issue-meta-val">{issue.pubDate || `${issue.issueYear || issue.year || '-'}`}</span>
                      </div>

                      <div className="issue-meta-item">
                        <span className="issue-meta-label">
                          <i className="bi bi-calendar-check me-1 text-muted"></i> Received Date:
                        </span>
                        <span className="issue-meta-val text-success">
                          {issue.receivedDate ? issue.receivedDate : <em className="text-muted fw-normal">Not received</em>}
                        </span>
                      </div>

                      <div className="issue-meta-item">
                        <span className="issue-meta-label">
                          <i className="bi bi-calendar-event me-1 text-muted"></i> Expected Date:
                        </span>
                        <span className="issue-meta-val">{issue.expDate || '-'}</span>
                      </div>

                      {issue.issuedTo && (
                        <div className="issue-meta-item">
                          <span className="issue-meta-label">
                            <i className="bi bi-person me-1 text-muted"></i> Issued To:
                          </span>
                          <span className="issue-meta-val">{issue.issuedTo}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Card Bottom Quick Actions */}
                  <div className="issue-card-actions justify-content-between align-items-center">
                    <div>
                      {issue.issueStatus !== 'Received' && !viewOnly ? (
                        <Button
                          className="btn-mark-received"
                          size="sm"
                          disabled={isRowReceiving}
                          onClick={(e) => handleQuickReceive(e, issue)}
                          title="Click to check in this issue today"
                        >
                          {isRowReceiving ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <>
                              <i className="bi bi-check-lg me-1"></i> Receive
                            </>
                          )}
                        </Button>
                      ) : (
                        <span className="text-success small fw-semibold">
                          <i className="bi bi-check2-all me-1"></i> Checked In
                        </span>
                      )}
                    </div>

                    <div className="d-flex gap-1">
                      <Button
                        variant="light"
                        className="btn-card-action"
                        size="sm"
                        onClick={(e) => handleViewArticles(e, issue)}
                        title="View articles linked to this issue"
                      >
                        <i className="bi bi-files"></i> Articles
                      </Button>

                      {!viewOnly && (
                        <Button
                          variant="light"
                          className="btn-card-action"
                          size="sm"
                          onClick={(e) => handleEditIssue(e, issue)}
                          title="Edit Issue Details"
                        >
                          <i className="bi bi-pencil"></i>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ================= TABLE VIEW ================= */
        <div className="issues-table-container">
          <div className="table-responsive">
            <Table hover className="issues-table align-middle">
              <thead>
                <tr>
                  <th>Vol / Issue</th>
                  <th>Publication Date</th>
                  <th>Received Date</th>
                  <th>Expected Date</th>
                  <th>Status</th>
                  <th>Seq</th>
                  <th>Issued To</th>
                  <th className="text-end">Quick Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredIssues.map((issue, idx) => {
                  const issueKey = issue.volissuesNo || `${issue.volNo}-${issue.issueNumber}`;
                  const isRowReceiving = actionLoadingId === issueKey;

                  return (
                    <tr key={issue.volissuesNo || idx}>
                      <td>
                        <div className="fw-bold text-dark">
                          Vol {issue.volNo ?? issue.volume ?? '-'}, Issue #{issue.issueNumber ?? '-'}
                        </div>
                        <small className="text-muted font-monospace">
                          {issue.volissuesNo || `${issue.volNo}${issue.issueNumber}`}
                        </small>
                      </td>
                      <td>{issue.pubDate || `${issue.issueYear || issue.year || '-'}`}</td>
                      <td>
                        {issue.receivedDate ? (
                          <span className="text-success fw-semibold">{issue.receivedDate}</span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>{issue.expDate || '-'}</td>
                      <td>{renderStatusBadge(issue.issueStatus)}</td>
                      <td>{issue.seq || '-'}</td>
                      <td>{issue.issuedTo || '-'}</td>
                      <td className="text-end">
                        <div className="d-flex justify-content-end align-items-center gap-1">
                          {issue.issueStatus !== 'Received' && !viewOnly && (
                            <Button
                              variant="success"
                              size="sm"
                              className="py-1 px-2 text-nowrap"
                              disabled={isRowReceiving}
                              onClick={(e) => handleQuickReceive(e, issue)}
                              title="Mark as Received"
                            >
                              {isRowReceiving ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <>
                                  <i className="bi bi-check2"></i> Receive
                                </>
                              )}
                            </Button>
                          )}

                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="py-1 px-2"
                            onClick={(e) => handleViewArticles(e, issue)}
                            title="View Articles"
                          >
                            <i className="bi bi-files"></i>
                          </Button>

                          {!viewOnly && (
                            <>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                className="py-1 px-2"
                                onClick={(e) => handleEditIssue(e, issue)}
                                title="Edit Issue"
                              >
                                <i className="bi bi-pencil"></i>
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="py-1 px-2"
                                onClick={(e) => handleDeleteClick(e, issue)}
                                title="Delete Issue & Articles"
                              >
                                <i className="bi bi-trash"></i>
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>
      )}

      {/* ================= MODAL: ADD / EDIT SINGLE ISSUE ================= */}
      <Modal show={showIndModal} onHide={handleIndClose} size="lg" centered backdrop="static">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="h5 fw-bold">
            <i className={`bi ${issueModalMode === 'Add' ? 'bi-plus-circle text-primary' : 'bi-pencil-square text-warning'} me-2`}></i>
            {issueModalMode === 'Add' ? 'Add Single Journal Issue' : 'Edit Journal Issue'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form onSubmit={handleSaveIssue}>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="journal-form-label">Volume Number *</Form.Label>
                  <Form.Control
                    type="number"
                    value={newIssue.volNo}
                    onChange={(e) => setNewIssue({ ...newIssue, volNo: e.target.value })}
                    required
                    min="1"
                    placeholder="e.g. 1"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="journal-form-label">Issue Number *</Form.Label>
                  <Form.Control
                    type="number"
                    value={newIssue.issueNumber}
                    onChange={(e) => setNewIssue({ ...newIssue, issueNumber: e.target.value })}
                    required
                    min="1"
                    placeholder="e.g. 1"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="journal-form-label">Publication Year *</Form.Label>
                  <Form.Control
                    type="number"
                    value={newIssue.issueYear}
                    onChange={(e) => setNewIssue({ ...newIssue, issueYear: e.target.value })}
                    required
                    min="1800"
                    max={new Date().getFullYear() + 5}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="journal-form-label">Publication Month</Form.Label>
                  <Form.Select
                    value={newIssue.issueMonth}
                    onChange={(e) => setNewIssue({ ...newIssue, issueMonth: e.target.value })}
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {new Date(2000, i).toLocaleString('default', { month: 'long' })}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="journal-form-label">Publication Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={newIssue.pubDate}
                    onChange={(e) => setNewIssue({ ...newIssue, pubDate: e.target.value })}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="journal-form-label">Expected Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={newIssue.expDate}
                    onChange={(e) => setNewIssue({ ...newIssue, expDate: e.target.value })}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="journal-form-label">Issue Status *</Form.Label>
                  <Form.Select
                    value={newIssue.issueStatus}
                    onChange={(e) => {
                      const selectedStatus = e.target.value;
                      const today = new Date().toISOString().split('T')[0];
                      setNewIssue({
                        ...newIssue,
                        issueStatus: selectedStatus,
                        receivedDate: selectedStatus === 'Received' && !newIssue.receivedDate ? today : newIssue.receivedDate
                      });
                    }}
                  >
                    <option value="Expected">Expected (In-transit)</option>
                    <option value="Received">Received (In Library)</option>
                    <option value="Missing">Missing (Claim needed)</option>
                    <option value="Damaged">Damaged</option>
                    <option value="Available">Available</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="journal-form-label mb-0">Received Date</Form.Label>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-decoration-none small text-primary"
                      onClick={() => setNewIssue({ ...newIssue, receivedDate: new Date().toISOString().split('T')[0] })}
                      title="Set Received Date to Today"
                    >
                      Set Today
                    </Button>
                  </div>
                  <Form.Control
                    type="date"
                    value={newIssue.receivedDate || ''}
                    onChange={(e) => setNewIssue({ ...newIssue, receivedDate: e.target.value })}
                  />
                  <Form.Text className="text-muted small">
                    Editable date to record when the issue was received at the library.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="journal-form-label">Sequence #</Form.Label>
                  <Form.Control
                    type="text"
                    value={newIssue.seq}
                    onChange={(e) => setNewIssue({ ...newIssue, seq: e.target.value })}
                    placeholder="e.g. 1"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="journal-form-label">Issued To</Form.Label>
                  <Form.Control
                    type="text"
                    value={newIssue.issuedTo}
                    onChange={(e) => setNewIssue({ ...newIssue, issuedTo: e.target.value })}
                    placeholder="Member / Department name"
                  />
                </Form.Group>
              </Col>

              <Col md={12} className="d-flex justify-content-end gap-2 mt-4">
                <Button variant="secondary" onClick={handleIndClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" className="journal-btn" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" /> Saving...
                    </>
                  ) : (
                    issueModalMode === 'Add' ? 'Add Issue' : 'Update Issue'
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ================= MODAL: QUICK RECEIVE ISSUE ================= */}
      <Modal show={showReceiveModal} onHide={handleReceiveModalClose} centered backdrop="static">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="h5 fw-bold d-flex align-items-center gap-2">
            <i className="bi bi-box-seam text-success"></i>
            Receive Journal Issue
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {receiveTargetIssue && (
            <Form onSubmit={handleConfirmReceive}>
              <div className="bg-light border rounded p-3 mb-3">
                <div className="fw-bold text-dark fs-6 mb-1">
                  {receiveTargetIssue.journalTitle || journalData?.journalTitle || 'Journal Issue'}
                </div>
                <div className="d-flex flex-wrap gap-2 text-muted small align-items-center">
                  <span className="badge bg-primary bg-opacity-10 text-primary">
                    Volume {receiveTargetIssue.volNo ?? receiveTargetIssue.volume ?? '-'}
                  </span>
                  <span className="badge bg-secondary bg-opacity-10 text-secondary">
                    Issue #{receiveTargetIssue.issueNumber ?? '-'}
                  </span>
                  {receiveTargetIssue.pubDate && (
                    <span>Published: <strong>{receiveTargetIssue.pubDate}</strong></span>
                  )}
                  {receiveTargetIssue.expDate && (
                    <span>Expected: <strong>{receiveTargetIssue.expDate}</strong></span>
                  )}
                </div>
              </div>

              <Form.Group className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Form.Label className="journal-form-label mb-0 fw-bold">
                    Received Date <span className="text-danger">*</span>
                  </Form.Label>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-decoration-none small text-primary"
                    onClick={() => setReceiveModalDate(new Date().toISOString().split('T')[0])}
                    title="Set to today's date"
                  >
                    Set to Today
                  </Button>
                </div>
                <Form.Control
                  type="date"
                  value={receiveModalDate}
                  onChange={(e) => setReceiveModalDate(e.target.value)}
                  required
                  autoFocus
                  className="form-control-lg fs-6"
                />
                <Form.Text className="text-muted small">
                  Defaulted to today's date. As part of the digital catalog update, you can edit this date to accurately record when this issue physically arrived at the library.
                </Form.Text>
              </Form.Group>

              <div className="d-flex align-items-center gap-2 text-success small mb-3">
                <i className="bi bi-check-circle-fill"></i>
                <span>Status will be updated to: <strong>Received (In Library)</strong></span>
              </div>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button variant="secondary" onClick={handleReceiveModalClose} disabled={isReceivingSubmit}>
                  Cancel
                </Button>
                <Button variant="success" type="submit" disabled={isReceivingSubmit} className="fw-bold">
                  {isReceivingSubmit ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check2 me-1"></i> Confirm & Receive
                    </>
                  )}
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>

      {/* ================= MODAL: BULK GENERATE ISSUES ================= */}
      <Modal show={showBulkModal} onHide={handleBulkClose} size="lg" centered backdrop="static">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="h5 fw-bold">
            <i className="bi bi-lightning-charge text-primary me-2"></i>
            Bulk Generate Journal Issues
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form onSubmit={handleBulkSubmit}>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="journal-form-label">Volume Number *</Form.Label>
                  <Form.Control
                    type="number"
                    value={bulkGeneration.volNo}
                    onChange={(e) => setBulkGeneration({ ...bulkGeneration, volNo: e.target.value })}
                    required
                    min="1"
                    placeholder="e.g. 1"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="journal-form-label">Start Issue Number *</Form.Label>
                  <Form.Control
                    type="number"
                    value={bulkGeneration.startIssueNumber}
                    onChange={(e) => setBulkGeneration({ ...bulkGeneration, startIssueNumber: e.target.value })}
                    required
                    min="1"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="journal-form-label">End Issue Number *</Form.Label>
                  <Form.Control
                    type="number"
                    value={bulkGeneration.endIssueNumber}
                    onChange={(e) => setBulkGeneration({ ...bulkGeneration, endIssueNumber: e.target.value })}
                    required
                    min="1"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="journal-form-label">Start Date *</Form.Label>
                  <Form.Control
                    type="date"
                    value={bulkGeneration.startDate}
                    onChange={(e) => setBulkGeneration({ ...bulkGeneration, startDate: e.target.value })}
                    required
                  />
                  <Form.Text className="text-muted">First issue publication date</Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="journal-form-label">End Date (Optional)</Form.Label>
                  <Form.Control
                    type="date"
                    value={bulkGeneration.endDate}
                    onChange={(e) => setBulkGeneration({ ...bulkGeneration, endDate: e.target.value })}
                  />
                  <Form.Text className="text-muted">Optional cutoff date</Form.Text>
                </Form.Group>
              </Col>

              <Col md={12}>
                <div className="bulk-preview-container">
                  <div className="bulk-preview-title">
                    <i className="bi bi-info-circle me-1"></i> Generation Preview
                  </div>
                  <div className="small text-dark">
                    <strong>Journal Frequency:</strong> {journalData?.frequency || 'Monthly'}<br />
                    {preview && preview.count > 0 ? (
                      <span>
                        Will generate <strong>{preview.count} issues</strong> for <strong>Volume {bulkGeneration.volNo || 'X'}</strong> (Issues #{preview.startNum} through #{preview.endNum}) sorted in descending order.
                      </span>
                    ) : (
                      <span className="text-danger">Please enter valid Volume and Issue range.</span>
                    )}
                  </div>
                </div>
              </Col>

              <Col md={12} className="d-flex justify-content-end gap-2 mt-4">
                <Button variant="secondary" onClick={handleBulkClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" className="journal-btn" disabled={isSubmitting || !preview || preview.count === 0}>
                  {isSubmitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" /> Generating...
                    </>
                  ) : (
                    `Generate ${preview?.count ? `${preview.count} Issues` : 'Issues'}`
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ================= MODAL: ADD ARTICLE ================= */}
      <Modal show={showArticleModal} onHide={handleArticleClose} size="lg" centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="h5 fw-bold">
            <i className="bi bi-file-earmark-plus text-primary me-2"></i>
            Add Article to Issue
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <ArticlesForm
            handleClose={handleArticleClose}
            viewOnly={viewOnly}
            formType="New"
          />
        </Modal.Body>
      </Modal>

      {/* ================= MODAL: DELETE ISSUE CONFIRMATION ================= */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered backdrop="static">
        <Modal.Header closeButton className="bg-light border-0 pb-0">
          <Modal.Title className="fw-bold fs-5 text-danger d-flex align-items-center gap-2">
            <i className="bi bi-exclamation-triangle-fill"></i>
            Confirm Issue Deletion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 pt-3">
          <p className="text-secondary mb-3">
            Are you sure you want to delete this journal issue and all of its corresponding articles? This action cannot be undone.
          </p>
          {issueToDelete && (
            <div className="p-3 bg-light rounded-3 border">
              <div className="fw-bold text-dark mb-1">
                Volume {issueToDelete.volNo ?? issueToDelete.volume ?? '-'}, Issue #{issueToDelete.issueNumber ?? '-'}
              </div>
              <div className="small text-secondary d-flex flex-column gap-1 mt-2">
                <div>
                  <strong>Issue Code:</strong>{' '}
                  <span className="font-monospace text-primary">
                    {issueToDelete.volissuesNo || `${issueToDelete.volNo}${issueToDelete.issueNumber}`}
                  </span>
                </div>
                {issueToDelete.pubDate && (
                  <div>
                    <strong>Publication Date:</strong> {issueToDelete.pubDate}
                  </div>
                )}
                {issueToDelete.issueStatus && (
                  <div>
                    <strong>Status:</strong> {issueToDelete.issueStatus}
                  </div>
                )}
                {journalData?.journalTitle && (
                  <div>
                    <strong>Journal:</strong> {journalData.journalTitle}
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 px-4 pb-4">
          <Button variant="outline-secondary" onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            className="d-flex align-items-center gap-2 shadow-sm"
          >
            {isDeleting ? (
              <>
                <Spinner animation="border" size="sm" />
                Deleting...
              </>
            ) : (
              <>
                <i className="bi bi-trash-fill"></i>
                Delete Issue & Articles
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
